import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { SettingsService } from '../../services/settings.services';
import { IMailboxSettings, IUser } from '../../models/settings.models';
import { DEFAULT_PAGE_SIZE } from 'src/app/config/global.config';
import { IPageCount, IPagination } from 'src/app/shared/models/page.model';
import { ConnectionDetailsModalComponent } from '../connection-details-modal/connection-details-modal.component';
import { Store } from '@ngrx/store';
import { Actions, ofType } from '@ngrx/effects';
import { AppState } from 'src/app/core/app.state';
import { FetchAllUsers, FetchAllUsersSuccess, FetchAllUsersError } from '../../store/settings.actions';
import { AccountService } from 'src/app/features/smart-inbox/services/account.service';
import { MailboxTagPreferenceService } from 'src/app/features/smart-inbox/services/mailbox-tag-preference.service';
import {
  DEFAULT_MAILBOX_TAG_DISPLAY,
  MAILBOX_TAG_DISPLAY_OPTIONS,
  MailboxTagDisplay,
} from 'src/app/features/smart-inbox/models/inbox.models';
import { CommonService } from 'src/app/shared/common.service';

// ─────────────────────────────────────────────────────────────────────────────
// SmartInboxSettingsComponent
// ─────────────────────────────────────────────────────────────────────────────
// Settings page for Smart Inbox: list mailboxes, open connection details modal,
// pagination. Responsibilities: load mailboxes, handle errors via i18n, open modal.
// ─────────────────────────────────────────────────────────────────────────────

/** Inline sync-error length; the full text stays available on hover. */
const SYNC_ERROR_SUMMARY_MAX_CHARS = 90;

@Component({
  selector: 'app-smart-inbox-settings',
  templateUrl: './smart-inbox-settings.component.html',
  styleUrls: ['./smart-inbox-settings.component.scss'],
})
export class SmartInboxSettingsComponent implements OnInit, OnDestroy {
  // ── Public state ─────────────────────────────────────────────────────────
  mailboxes: IMailboxSettings[] = [];
  users: IUser[] = [];
  metadata: IPagination | null = null;
  loading = false;
  error: string | null = null;
  page: IPageCount = { number: 1, size: DEFAULT_PAGE_SIZE };
  tab: 'connections' | 'replies' = 'connections';

  /** Where the recipient-mailbox tag is shown in Smart Inbox. */
  mailboxTagDisplay: MailboxTagDisplay = DEFAULT_MAILBOX_TAG_DISPLAY;
  readonly MAILBOX_TAG_DISPLAY_OPTIONS = MAILBOX_TAG_DISPLAY_OPTIONS;

  private destroy$ = new Subject<void>();
  private mailboxesLoaded = false;
  private usersLoaded = false;
  private initialLoad = true;
  private pendingMailboxId: number | null = null;

  constructor(
    private settingsService: SettingsService,
    private dialog: MatDialog,
    private translate: TranslateService,
    private store: Store<AppState>,
    private actions$: Actions,
    private accountService: AccountService,
    private route: ActivatedRoute,
    private commonService: CommonService,
    private mailboxTagPreference: MailboxTagPreferenceService,
  ) {}

  /** Persists the tag preference; Smart Inbox picks it up without a reload. */
  onMailboxTagDisplayChange(display: MailboxTagDisplay): void {
    this.mailboxTagDisplay = display;
    this.mailboxTagPreference.set(display);
  }

  ngOnInit(): void {
    this.mailboxTagDisplay = this.mailboxTagPreference.current;

    this.store.select((state) => state.settings.allUsers).pipe(takeUntil(this.destroy$)).subscribe((users) => {
      this.users = users;
    });

    this.loading = true;
    this.mailboxesLoaded = false;
    this.usersLoaded = false;
    this.initialLoad = true;

    this.actions$
      .pipe(
        ofType(FetchAllUsersSuccess, FetchAllUsersError),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        this.usersLoaded = true;
        if (this.mailboxesLoaded) {
          this.loading = false;
          this.initialLoad = false;
        }
      });

    this.route.queryParams.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      const id = params['mailboxId'];
      if (id) {
        this.pendingMailboxId = +id;
      }
      const tabParam = params['tab'];
      if (tabParam === 'replies' || tabParam === 'connections') {
        this.tab = tabParam;
      }
    });

    this.loadMailboxes();
    this.store.dispatch(FetchAllUsers({}));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ── Data load ─────────────────────────────────────────────────────────────
  loadMailboxes(): void {
    if (!this.initialLoad) {
      this.loading = true;
    }
    this.error = null;
    this.settingsService
      .listInboxMailboxes({ limit: this.page.size, page: this.page.number })
      .subscribe({
        next: (res) => {
          this.mailboxes = Array.isArray(res.data) ? res.data : [];
          this.metadata = res.metadata ?? null;
          this.mailboxesLoaded = true;
          if (this.initialLoad && this.usersLoaded) {
            this.loading = false;
            this.initialLoad = false;
          } else if (!this.initialLoad) {
            this.loading = false;
          }
          this.openPendingMailbox();
        },
        error: (err) => {
          this.error = err?.error?.error ?? this.translate.instant('settings.smart_inbox_error_load_mailboxes');
          this.mailboxesLoaded = true;
          if (this.initialLoad && this.usersLoaded) {
            this.loading = false;
            this.initialLoad = false;
          } else if (!this.initialLoad) {
            this.loading = false;
          }
        },
      });
  }

  toPage(page: IPageCount): void {
    this.page = { ...page };
    this.loadMailboxes();
  }

  setTab(tab: 'connections' | 'replies'): void {
    this.tab = tab;
  }

  // ── Actions ──────────────────────────────────────────────────────────────
  openConnectionDetails(mailbox: IMailboxSettings): void {
    const dialogRef = this.dialog.open(ConnectionDetailsModalComponent, {
      data: { mailbox },
      panelClass: 'connection-details-dialog',
      disableClose: false,
    });
    dialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe((reload) => {
      if (reload) this.loadMailboxes();
    });
  }

  connectAccount(): void {
    this.accountService.openConnectionWizard().subscribe((success) => {
      if (!success) return;
      // The backend enqueues the initial sync itself, but this list is fetched
      // before that sync has produced anything — say so, instead of leaving the
      // user with a mailbox that looks empty for no stated reason.
      this.commonService.showToast(
        this.translate.instant('settings.smart_inbox_connected_sync_started'),
        'INFO',
        { duration: 8000, verticalPosition: 'bottom', horizontalPosition: 'right' },
      );
      this.loadMailboxes();
    });
  }

  private openPendingMailbox(): void {
    if (this.pendingMailboxId == null) return;
    const mailbox = this.mailboxes.find((m) => m.id === this.pendingMailboxId);
    this.pendingMailboxId = null;
    if (mailbox) {
      this.openConnectionDetails(mailbox);
    }
  }

  // ── Labels (i18n) ─────────────────────────────────────────────────────────
  providerLabel(provider: string): string {
    const map: Record<string, string> = {
      gmail: 'Gmail',
      o365: 'Outlook',
      exchange: 'Exchange',
    };
    return map[provider] ?? provider;
  }

  sharingLabel(mailbox: IMailboxSettings): string {
    return mailbox.mailbox_type === 'team'
      ? this.translate.instant('settings.sharing_team')
      : this.translate.instant('settings.sharing_personal');
  }

  canViewLabel(mailbox: IMailboxSettings): string {
    if (mailbox.mailbox_type === 'personal') return this.translate.instant('settings.owner');
    const n = mailbox.can_view_ids?.length ?? 0;
    return n > 0 ? `${n} <span class="total_users">/${this.users.length}</span>` : this.translate.instant('settings.smart_inbox_can_view_all') + ' <span class="total_users">(' + this.users.length + ')</span>';
  }

  canSendLabel(mailbox: IMailboxSettings): string {
    if (mailbox.mailbox_type === 'personal') return this.translate.instant('settings.owner');
    const n = mailbox.can_send_ids?.length ?? 0;
    return n > 0 ? `${n} <span class="total_users">/${this.users.length}</span>` : this.translate.instant('settings.smart_inbox_can_view_all') + ' <span class="total_users">(' + this.users.length + ')</span>';
  }

  statusLabel(mailbox: IMailboxSettings): string {
    return mailbox.connection_status === 'connected'
      ? this.translate.instant('settings.status_connected')
      : this.translate.instant('settings.status_disconnected');
  }

  // ── Sync state ────────────────────────────────────────────────────────────
  // Connection state and sync state are different facts: a connected mailbox can
  // be failing to sync. Settings is where users come when the inbox looks wrong,
  // so both are shown, and a failure says why.

  isSyncing(mailbox: IMailboxSettings): boolean {
    return mailbox.sync_status === 'syncing';
  }

  hasSyncError(mailbox: IMailboxSettings): boolean {
    return mailbox.sync_status === 'error';
  }

  syncStatusLabel(mailbox: IMailboxSettings): string {
    switch (mailbox.sync_status) {
      case 'syncing': return this.translate.instant('settings.smart_inbox_sync_syncing');
      case 'error': return this.translate.instant('settings.smart_inbox_sync_error');
      case 'paused': return this.translate.instant('settings.smart_inbox_sync_paused');
      default: return '';
    }
  }

  /** Reason shown inline; the untruncated text goes in the tooltip. */
  syncErrorSummary(mailbox: IMailboxSettings): string {
    const reason = mailbox.last_sync_error?.trim();
    if (!reason) return this.translate.instant('settings.smart_inbox_sync_error_unknown');
    return reason.length > SYNC_ERROR_SUMMARY_MAX_CHARS
      ? `${reason.slice(0, SYNC_ERROR_SUMMARY_MAX_CHARS)}…`
      : reason;
  }

  syncErrorFullText(mailbox: IMailboxSettings): string {
    return mailbox.last_sync_error?.trim()
      || this.translate.instant('settings.smart_inbox_sync_error_unknown');
  }

  lastSyncLabel(mailbox: IMailboxSettings): string {
    if (!mailbox.last_sync_at) return '';
    const date = new Date(mailbox.last_sync_at);
    const hours = Math.floor((Date.now() - date.getTime()) / (1000 * 60 * 60));
    if (hours < 1) return this.translate.instant('settings.smart_inbox_last_sync_just_now');
    if (hours === 1) return this.translate.instant('settings.smart_inbox_last_sync_one_hour');
    return this.translate.instant('settings.smart_inbox_last_sync_hours', { hours });
  }
}
